import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import VSLogo from "@/components/VSLogo";
import { Button } from "@/components/ui/button";
import { loadUser, clearAuth } from "@/lib/api";
import { LogOut, LayoutDashboard, Menu, X } from "lucide-react";

export default function PublicHeader() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const user = loadUser();
  const [open, setOpen] = React.useState(false);

  const logout = () => { clearAuth(); nav("/"); };
  const dashUrl = user?.role === "candidate" ? "/candidate/dashboard" : "/admin/dashboard";

  const NavLink = ({ to, children }) => (
    <Link to={to} data-testid={`nav-${to.replace(/\W+/g,'-')}`}
      className={`text-sm font-medium transition-colors ${pathname === to ? "text-[#0A2540]" : "text-slate-600 hover:text-[#0A2540]"}`}>
      {children}
    </Link>
  );

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <VSLogo size={38} showWordmark />
          <nav className="hidden md:flex items-center gap-6">
            <NavLink to="/">Home</NavLink>
            <NavLink to="/jobs">Jobs</NavLink>
            <Link to="/jobs?experience_category=Fresher" className="text-sm font-medium text-slate-600 hover:text-[#0A2540]">Students & Graduates</Link>
            <Link to="/jobs?experience_category=3-5%20Years" className="text-sm font-medium text-slate-600 hover:text-[#0A2540]">Experienced</Link>
            <a href="/#life" className="text-sm font-medium text-slate-600 hover:text-[#0A2540]">Life at VS</a>
            <Link to="/career-events" className="text-sm font-medium text-slate-600 hover:text-[#0A2540]">Events</Link>
          </nav>
        </div>
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Button variant="outline" size="sm" onClick={() => nav(dashUrl)} data-testid="header-dashboard">
                <LayoutDashboard className="w-4 h-4 mr-1.5" /> Dashboard
              </Button>
              <Button size="sm" variant="ghost" onClick={logout} data-testid="header-logout">
                <LogOut className="w-4 h-4 mr-1.5" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" asChild data-testid="header-login">
                <Link to="/login">Sign in</Link>
              </Button>
              <Button size="sm" asChild className="bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="header-register">
                <Link to="/register">Get started</Link>
              </Button>
            </>
          )}
        </div>
        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="header-mobile-toggle">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-3">
          <Link to="/" className="block text-sm" onClick={() => setOpen(false)}>Home</Link>
          <Link to="/jobs" className="block text-sm" onClick={() => setOpen(false)}>Jobs</Link>
          {user ? (
            <>
              <Link to={dashUrl} className="block text-sm">Dashboard</Link>
              <button onClick={logout} className="block text-sm text-red-600">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block text-sm">Sign in</Link>
              <Link to="/register" className="block text-sm font-semibold">Get started</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
